import { Product } from "api/schema/productsSchema";

import { paths } from "components/App/paths";

import {
  BankFeedsIcon,
  LendingIcon,
  SupplierEnablementIcon,
  SyncForCommerceIcon,
  SyncForExpensesIcon,
  SyncForPayablesIcon,
  SyncForPayrollIcon,
} from "../../components/ProductCards/icons";
import { productIds } from "./productIds";

interface ProductDetails {
  onboardingCopy: string;
  homePageCopy: string;
  Icon: React.FC;
  colorTheme: "orange" | "purple" | "pink" | "blue" | "green" | "yellow";
  learnMoreLinkHref: string;
  apiDocsHref: string;
  productPagePath: string | undefined;
  bannerImageHref: string;
  videoLinkHref?: string;
}

export const getProductDetails = (
  product: Product | undefined
): ProductDetails => {
  if (product === undefined) {
    return {
      onboardingCopy: "Unknown",
      homePageCopy: "Unknown",
      Icon: SyncForCommerceIcon,
      colorTheme: "yellow",
      learnMoreLinkHref: "https://docs.codat.io/",
      apiDocsHref: getApiDocsLink("codat"),
      productPagePath: undefined,
      bannerImageHref: bannerImageHref("Unknown"),
      videoLinkHref: "https://www.youtube.com/embed/4zLgo0iP6MI?autoplay=1",
    };
  }
  const { productId, name } = product;
  switch (productId) {
    case productIds.syncForCommerce:
      return {
        onboardingCopy:
          "Effortlessly reflect data from PoS and eCommerce platforms your customers use in their accounting systems.",
        homePageCopy:
          "Embedded accounting integrations for PoS and eCommerce platforms.",
        Icon: SyncForCommerceIcon,
        colorTheme: "yellow",
        learnMoreLinkHref: getProductInfoLink("commerce"), //docs
        apiDocsHref: getApiDocsLink("sync-for-commerce"), //api
        productPagePath: paths.products.SYNC_FOR_COMMERCE,
        bannerImageHref: bannerImageHref(productId),
      };
    case productIds.syncForExpensesV1:
      return {
        onboardingCopy:
          "Streamline expense reconciliation from corporate card and expense management systems to your customers' accounting platforms.",
        homePageCopy:
          "Embedded accounting integrations for corporate card providers.",
        Icon: SyncForExpensesIcon,
        colorTheme: "blue",
        learnMoreLinkHref: getProductInfoLink("expenses"),
        apiDocsHref: getApiDocsLink("sync-for-expenses"),
        productPagePath: paths.products.SYNC_FOR_EXPENSES,
        bannerImageHref: bannerImageHref(productId),
      };
    case productIds.syncForExpensesV2:
      return {
        onboardingCopy:
          "Streamline expense reconciliation from corporate card and expense management systems to your customers' accounting platforms.",
        homePageCopy:
          "Embedded accounting integrations for corporate card providers.",
        Icon: SyncForExpensesIcon,
        colorTheme: "blue",
        learnMoreLinkHref: getProductInfoLink("expenses"),
        apiDocsHref: getApiDocsLink("sync-for-expenses"),
        productPagePath: paths.products.SYNC_FOR_EXPENSES,
        bannerImageHref: bannerImageHref(productId),
      };
    case productIds.syncForPayroll:
      return {
        onboardingCopy:
          "Simplify payroll reconciliation from your application to your customers' accounting platforms.",
        homePageCopy:
          "Manage payroll reconciliation with your customers' accounting platform.",
        Icon: SyncForPayrollIcon,
        colorTheme: "purple",
        learnMoreLinkHref: getProductInfoLink("payroll"),
        apiDocsHref: getApiDocsLink("sync-for-payroll"),
        productPagePath: undefined,
        bannerImageHref: bannerImageHref(productId),
      };
    case productIds.payablesV1:
      return {
        onboardingCopy:
          "Make it easier for your customers to manage and pay suppliers from a single interface.",
        homePageCopy:
          "Integrate with your customers' accounting software, and keep their supplier information, invoices, and payments in sync.",
        Icon: SyncForPayablesIcon,
        colorTheme: "pink",
        learnMoreLinkHref: getProductInfoLink("payables"),
        apiDocsHref: getApiDocsLink("sync-for-payables"),
        productPagePath: undefined,
        bannerImageHref: bannerImageHref(productId),
      };
    case productIds.payablesV2:
      return {
        onboardingCopy:
          "Make it easier for your customers to manage and pay suppliers from a single interface.",
        homePageCopy:
          "Integrate with your customers' accounting software, and keep their supplier information, invoices, and payments in sync.",
        Icon: SyncForPayablesIcon,
        colorTheme: "pink",
        learnMoreLinkHref: getProductInfoLink("payables"),
        apiDocsHref: getApiDocsLink("sync-for-payables"),
        productPagePath: undefined,
        bannerImageHref: bannerImageHref(productId),
      };
    case productIds.bankFeeds:
      return {
        onboardingCopy:
          "Enable your small business customers to set up bank feeds from your application to accounting platforms they are already using.",
        homePageCopy:
          "Integrate with your customers' accounting software, and keep their supplier information, invoices, and payments in sync.",
        Icon: BankFeedsIcon,
        colorTheme: "green",
        learnMoreLinkHref: getProductInfoLink("bank-feeds"),
        apiDocsHref: getApiDocsLink("bank-feeds"),
        productPagePath: undefined,
        bannerImageHref: bannerImageHref(productId),
      };
    case productIds.lending:
      return {
        onboardingCopy:
          "Make smarter credit decisions on small businesses by pulling your customers' latest data from the systems they are already using.",
        homePageCopy:
          "Lending API helps you make smarter credit decisions on small businesses by enabling you to pull your customers' latest data from the operating systems they are already using. You can use that data for automating decisioning and surfacing new insights on the customer, all via one API.",
        Icon: LendingIcon,
        colorTheme: "orange",
        learnMoreLinkHref: getProductInfoLink("lending"),
        apiDocsHref: getApiDocsLink("lending"),
        productPagePath: undefined,
        bannerImageHref: bannerImageHref(productId),
      };
    case productIds.spendInsights:
      return {
        onboardingCopy:
          "Manage comprehensive and actionable vendor and spend data from your clients to grow commercial card volume in B2B payments",
        homePageCopy: `Use ${name} to access your clients' accounts payable data and understand their spend and supplier activity. You can identify suppliers eligible for your clients' virtual card programs, run targeted supplier outreach campaigns on your clients' behalf, and grow your commercial card volume in B2B payments.`,
        Icon: SupplierEnablementIcon,
        colorTheme: "purple", // not important as it is not used for self-serve clients
        learnMoreLinkHref: getProductInfoLink("spend-insights"),
        apiDocsHref: getApiDocsLink("spend-insights"),
        productPagePath: paths.products.SPEND_INSIGHTS,
        bannerImageHref: bannerImageHref(productId),
      };
    default:
      return {
        onboardingCopy: "Unknown",
        homePageCopy: "Unknown",
        Icon: SyncForCommerceIcon,
        colorTheme: "yellow",
        learnMoreLinkHref: "https://docs.codat.io/",
        apiDocsHref: getApiDocsLink("codat"),
        productPagePath: undefined,
        bannerImageHref: bannerImageHref(productId),
        videoLinkHref: "https://www.youtube.com/embed/4zLgo0iP6MI?autoplay=1",
      };
  }
};

const getProductInfoLink = (
  productName:
    | "commerce"
    | "expenses"
    | "payroll"
    | "payables"
    | "bank-feeds"
    | "lending"
    | "spend-insights"
) => `https://docs.codat.io/${productName}/overview`;
const getApiDocsLink = (
  apiProduct:
    | "sync-for-commerce"
    | "sync-for-expenses"
    | "sync-for-payroll"
    | "sync-for-payables"
    | "bank-feeds"
    | "lending"
    | "spend-insights"
    | "codat"
) => `https://docs.codat.io/${apiProduct}-api#/`;

const bannerImageHref = (productId: string) =>
  `https://static.codat.io/public/products/bannerImages/${productId}.png`;
